import { jwtDecode } from 'jwt-decode';
import ssoAuth from './ssoAuth';

/**
 * 从token中解析账号类型，并同步到localStorage
 * @returns {string|null} 账号类型或null
 */
export const getAccountTypeFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const decodedToken = jwtDecode(token);
    const accountType = decodedToken.accountType;
    
    if (accountType) {
      // 同步到localStorage以确保一致性
      const currentAccountType = localStorage.getItem('accountType');
      if (currentAccountType !== accountType) {
        localStorage.setItem('accountType', accountType);
      }
      return accountType;
    }
  } catch (error) {
    console.error('解析token中的账号类型失败:', error);
  }
  
  // fallback到localStorage
  return localStorage.getItem('accountType');
};

/**
 * 从token中解析用户角色，并同步到localStorage
 * @returns {string|null} 用户角色或null
 */
export const getUserRoleFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const decodedToken = jwtDecode(token);
    const role = decodedToken.role;
    
    if (role) {
      // 同步到localStorage以确保一致性
      const currentRole = localStorage.getItem('role');
      if (currentRole !== role) {
        localStorage.setItem('role', role);
      }
      return role;
    }
  } catch (error) {
    console.error('解析token中的用户角色失败:', error);
  }
  
  // fallback到localStorage
  return localStorage.getItem('role');
};

/**
 * 从token中解析用户ID
 * @returns {string|null} 用户ID或null
 */
export const getUserIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken.id || null;
  } catch (error) {
    console.error('解析token中的用户ID失败:', error);
    return null;
  }
};

/**
 * 检查是否为测试用户（通过ID判断）
 * @returns {boolean} 是否为测试用户
 */
export const isTestUser = () => {
  const userId = getUserIdFromToken();
  // 指定的测试用户ID
  const TEST_USER_ID = '66eb98e358c1a76dac45558b';
  return userId === TEST_USER_ID;
};

/**
 * 检查用户是否已通过认证
 * @returns {boolean} 认证状态
 */
export const isUserAuthenticated = () => {
  // 首先检查传统token
  const token = localStorage.getItem('token');
  if (token) {
  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    
    // 检查token是否过期
      if (decodedToken.exp > currentTime) {
        return true;
      }
  } catch (error) {
      console.error('验证传统token失败:', error);
    }
  }

  // 如果传统token无效，检查SSO token
  return ssoAuth.isLoggedIn();
};

/**
 * 清除所有认证相关数据
 */
export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  localStorage.removeItem('accountType');
  localStorage.removeItem('permissions');
  localStorage.removeItem('parentUsername');
  // 同时清除SSO数据
  ssoAuth.clearStorage();
};

/**
 * 执行完整的登出流程
 * @param {Function} logoutAPI - 登出API调用函数
 * @param {Function} websocketDisconnect - WebSocket断开连接函数
 * @returns {Promise<Object>} 登出结果
 */
export const performLogout = async (logoutAPI, websocketDisconnect) => {
  try {
    // 1. 调用后端登出接口
    const result = await logoutAPI();
    
    // 2. 断开WebSocket连接
    if (websocketDisconnect) {
      websocketDisconnect();
    }
    
    // 3. 清除本地认证数据
    clearAuthData();
    
    return {
      success: true,
      message: result.message || '登出成功',
      sessionStatus: result.sessionStatus
    };
    
  } catch (error) {
    // 即使接口调用失败，也要清理本地数据
    if (websocketDisconnect) {
      websocketDisconnect();
    }
    clearAuthData();
    
    return {
      success: false,
      message: '登出处理完成（接口调用失败但本地数据已清理）',
      error: error.message
    };
  }
}; 